import { Alert } from "react-native";
import * as Sentry from "@sentry/react-native";
import type { Router } from "expo-router";

import EnvironmentVariablesLoader from "./environment";
import { sendDiagnosticsToSentry } from "./diagnostics";
import { isMarketingMode } from "./expo";

import { ApiError, ApiErrorCategory } from "@/schemas/api/error";
import type { AppUserSession } from "@/schemas/auth/session";

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

let isAlertShowing = false;

export function alertUnexpectedApiError(error: ApiError): void {
  // Suppress error alerts in marketing mode
  if (isMarketingMode()) {
    return;
  }

  // If an alert is already showing, don't show another one
  if (isAlertShowing) {
    return;
  }

  // Mark that we're showing an alert
  isAlertShowing = true;

  const resetFlag = (): void => {
    isAlertShowing = false;
  };

  switch (error.category) {
    case ApiErrorCategory.CLIENT_ERROR:
      Alert.alert(
        "Client Error",
        error.message,
        [{ text: "OK", onPress: resetFlag }],
        {
          onDismiss: resetFlag, // Reset the flag if alert is dismissed by tapping outside (Android)
        },
      );
      break;
    default:
      if (EnvironmentVariablesLoader.isProduction()) {
        Sentry.captureException(error);
      }
      // const defaultMessage =
      //   "Something went wrong. Please try again or reach out to support@getkaiapp.com";
      Alert.alert(
        "Error",
        error.message, // TODO: This is a hack to show the error message. Error handling needs to be redesigned..
        [{ text: "OK", onPress: resetFlag }],
        {
          onDismiss: resetFlag,
        },
      );
  }
}
/**
 * Shows a network connection error alert with retry option
 * @param retryAction Function to call when user taps "Try Again"
 * @param signOutAction Optional function to call when user taps "Sign Out"
 */

export async function showNetworkErrorAlert(
  retryAction: () => void,
  session: AppUserSession | null,
  signOutAction?: () => void,
): Promise<void> {
  // Suppress error alerts in marketing mode
  if (isMarketingMode()) {
    return;
  }

  // If an alert is already showing, don't show another one
  if (isAlertShowing) {
    return;
  }

  // Mark that we're showing an alert
  isAlertShowing = true;

  const resetFlag = (): void => {
    isAlertShowing = false;
  };

  const buttons = [
    {
      text: "Try Again",
      onPress: (): void => {
        resetFlag();
        retryAction();
      },
    },
    {
      text: "Send Diagnostics",
      onPress: async (): Promise<void> => {
        resetFlag();
        await sendDiagnosticsToSentry(session);
        retryAction();
      },
    },
  ];

  if (signOutAction) {
    buttons.push({
      text: "Sign Out",
      onPress: (): void => {
        resetFlag();
        signOutAction();
      },
    });
  }

  Alert.alert(
    "Connection Issue",
    "Please check your network connection and try again.",
    buttons,
    {
      onDismiss: resetFlag,
    },
  );
}

export function showUnconfirmedAccountAlert(router: Router): void {
  // Suppress error alerts in marketing mode
  if (isMarketingMode()) {
    return;
  }

  if (isAlertShowing) {
    return;
  }

  // Mark that we're showing an alert
  isAlertShowing = true;

  const resetFlag = (): void => {
    isAlertShowing = false;
  };

  Alert.alert(
    "Account Not Confirmed",
    "Your account needs to be verified before signing in. Please check your email for a verification code.",
    [
      {
        text: "Cancel",
        style: "cancel",
        onPress: resetFlag,
      },
      {
        text: "Verify Now",
        onPress: (): void => {
          resetFlag();
          router.replace("/(authentication)/verify-account");
        },
      },
    ],
    {
      onDismiss: resetFlag, // Reset the flag if alert is dismissed by tapping outside (Android)
    },
  );
}
